import { Injectable, signal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import type { TableDataResponsePaginatedDTO } from '../../dtos/general.dto';
import type { UserFilterDTO } from '../../dtos/user-filter.dto';
import type { RegisterPayloadDTO, UserDTO, UserUpdateDTO } from '../../dtos/user.dto';
import { HttpWrapperService } from '../http-wrapper/http-wrapper.service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private readonly baseUrl = `${environment.url}/users`;
    private _currentUser = signal<UserDTO | null>(null);

    // Signal público de solo lectura
    get currentUser() {
        return this._currentUser.asReadonly();
    }

    constructor(private readonly http: HttpWrapperService) {}

    async getCurrentUserData(): Promise<UserDTO> {
        const user = await this.http.get<UserDTO>(`${this.baseUrl}/me`);
        this._currentUser.set(user);
        return user;
    }

    getCurrentUser(): UserDTO | null {
        return this._currentUser();
    }

    refreshCurrentUser(): Promise<UserDTO> {
        return this.getCurrentUserData();
    }

    getAllUsers(): Promise<UserDTO[]> {
        return this.http.get<UserDTO[]>(`${this.baseUrl}`);
    }

    getAllUsersPaginated(filters?: UserFilterDTO): Promise<TableDataResponsePaginatedDTO<UserDTO>> {
        return this.http.get<TableDataResponsePaginatedDTO<UserDTO>>(`${this.baseUrl}`, { params: filters });

    }

    getUserById(userId: number): Promise<UserDTO> {
        return this.http.get<UserDTO>(`${this.baseUrl}/${userId}`);
    }

    registerUser(userData: RegisterPayloadDTO): Promise<UserDTO> {
        return this.http.post<UserDTO>(`${environment.url}/auth/register`, userData);
    }

    async updateCurrentUserData(userData: UserDTO): Promise<UserDTO> {
        const updatedUser = await this.http.put<UserDTO>(`${this.baseUrl}/me`, userData);
        this._currentUser.set(updatedUser);
        return updatedUser;
    }

    async updateUser(idUser: number, userData: UserUpdateDTO): Promise<UserDTO> {
        const updatedUser = await this.http.put<UserDTO>(`${this.baseUrl}/${idUser}`, userData);
        const currentUser = this.getCurrentUser();
        // Si se actualiza el usuario actual, refrescar sus datos
        if (currentUser && currentUser.user_id === idUser) {
            // Usar los datos completos de la respuesta si están disponibles, sino refrescar desde el servidor
            if (updatedUser && 'user_id' in updatedUser) {
                this._currentUser.set(updatedUser as UserDTO);
            } else {
                await this.refreshCurrentUser();
            }
        }
        return updatedUser;
    }

    deleteUser(idUser: number): Promise<void> {
        return this.http.delete<void>(`${this.baseUrl}/${idUser}`);
    }

    clearCurrentUser(): void {
        this._currentUser.set(null);
    }

    getAllBlocks(page?: number, pageSize?: number): Promise<TableDataResponsePaginatedDTO<any>> {
        const params: any = {};
        if (page) params.page = page;
        if (pageSize) params.pageSize = pageSize;
        return this.http.get<TableDataResponsePaginatedDTO<any>>(`${this.baseUrl}/blocks/all`, { params });
    }

    unblockUserByAdmin(blockId: number): Promise<void> {
        return this.http.delete<void>(`${this.baseUrl}/blocks/${blockId}`);
    }
}
